import { Link } from "react-router";

/**
 * Home page hero banner. Animated gradient background with neon accents,
 * floating decorative orbs, bold positioning statement, and dual CTAs.
 * Pure CSS animations — no JS runtime cost.
 */
export function Hero() {
	return (
		<section className="relative overflow-hidden rounded-2xl border border-border">
			{/* Animated gradient underlay */}
			<div
				className="absolute inset-0 animate-gradient opacity-80"
				style={{
					background:
						"linear-gradient(135deg, rgba(0,255,136,0.08), rgba(99,102,241,0.12), rgba(255,107,53,0.08), rgba(0,255,136,0.08))",
					backgroundSize: "200% 200%",
				}}
				aria-hidden="true"
			/>
			{/* Surface overlay for depth */}
			<div
				className="absolute inset-0 bg-gradient-to-br from-surface-raised/90 via-surface/80 to-background/90"
				aria-hidden="true"
			/>

			{/* Floating decorative orbs */}
			<div
				className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-primary/15 blur-3xl animate-float pointer-events-none"
				aria-hidden="true"
			/>
			<div
				className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-purple/12 blur-3xl animate-float-slow pointer-events-none"
				aria-hidden="true"
			/>
			<div
				className="absolute top-1/2 right-1/4 h-32 w-32 rounded-full bg-accent/10 blur-2xl animate-pulse-glow pointer-events-none"
				aria-hidden="true"
			/>

			<div className="relative px-6 py-14 md:px-14 md:py-24">
				<div className="max-w-3xl">
					{/* Status badge */}
					<span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs font-semibold uppercase tracking-wider text-primary mb-5">
						<span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
						20+ game templates and counting
					</span>

					{/* Headline */}
					<h1 className="font-display font-extrabold text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-ink">
						Play games built by{" "}
						<span className="bg-gradient-to-r from-primary via-accent to-purple bg-clip-text text-transparent">
							creators like you
						</span>
					</h1>

					{/* Subheading */}
					<p className="mt-6 text-base md:text-lg lg:text-xl text-ink-muted max-w-2xl leading-relaxed">
						No downloads. No accounts. Polished games across 20
						genres — all free, all playable in your browser, all
						remixable.
					</p>

					{/* CTAs */}
					<div className="mt-10 flex flex-wrap items-center gap-4">
						<Link
							to="/browse"
							className="group inline-flex items-center gap-2 h-12 px-7 rounded-full bg-primary text-background font-bold text-base hover:shadow-[0_0_40px_rgba(0,255,136,0.4)] transition-all duration-300"
						>
							Browse all games
							<svg
								viewBox="0 0 24 24"
								fill="currentColor"
								className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
								aria-hidden="true"
							>
								<path d="M13 5l7 7-7 7-1.4-1.4L16.2 13H4v-2h12.2l-4.6-4.6z" />
							</svg>
						</Link>
						<a
							href="https://genieforge.io"
							className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-surface/60 border border-border text-ink font-bold text-base hover:border-purple hover:text-purple transition-all duration-300"
						>
							<svg
								viewBox="0 0 24 24"
								fill="currentColor"
								className="h-4 w-4"
								aria-hidden="true"
							>
								<path d="M12 2 15 9l7 .8-5.3 4.7L18.2 22 12 18.3 5.8 22l1.5-7.5L2 9.8 9 9z" />
							</svg>
							Make your own
						</a>
					</div>
				</div>
			</div>
		</section>
	);
}
